import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { DataSourceOrdenacaoInsensivel } from '../../../../shared/helpers/ordenacao-tabela.helper';
import { RouterModule } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { MarcaService } from '../../../../shared/services/apis/produto/marca.service';
import { MarcaStruct } from '../../../../shared/services/structs/produto/marca.struct';
import { UtilService } from '../../../../shared/services/util.service';
import { LoadingBoxDefaultListComponent } from '../../../../shared/components/loading-box-default-list/loading-box-default-list.component';

@Component({
  selector: 'app-config-marcas',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    HeaderComponent,
    EmptyListComponent,
    RouterModule,
    LoadingBoxDefaultListComponent,
  ],
  templateUrl: './config-marcas.component.html',
  styleUrl: './config-marcas.component.css',
})
export class ConfigMarcasComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Cadastros;

  public model: FormGroup;
  public _paginaAtual: number = 1;
  public _tamanhoPagina: number = 20;
  public _totalRegistros: number = 0;

  public _dataSource = new DataSourceOrdenacaoInsensivel<MarcaStruct>();
  public _isLoading: boolean = false;

  public displayedColumns: string[] = ['select', 'nome', 'actions'];

  public selection = new SelectionModel<MarcaStruct>(true, []);

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this._dataSource.sort = sort;
  }

  constructor(
    private formBuilder: FormBuilder,
    private marcaService: MarcaService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private utilService: UtilService
  ) {}

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      pesquisa: [''],
    });

    this.loadFilters();
    this.carregarMarcas();
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/configuracoes/cadastros/marcas/cadastrar',
      '/configuracoes/cadastros/marcas/cadastrar/',
    ]);
  }

  private loadFilters(): void {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }

  public carregarMarcas(reset: boolean = false): void {
    this._isLoading = true;
    const nome = this.model.get('pesquisa')?.value || '';

    this.marcaService.getAll(nome, this._paginaAtual, this._tamanhoPagina).subscribe({
      next: response => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show(
            'Erro',
            response.errorDescription || 'Erro ao carregar marcas',
            AlertType.error
          );
          return;
        }

        this._totalRegistros = response.totalRegistros;

        if (reset) {
          this._dataSource.data = response.listaMarcas;
          this.selection.clear();
        } else {
          this._dataSource.data = [...this._dataSource.data, ...response.listaMarcas];
        }
      },
      error: err => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', err.message || 'Erro ao carregar marcas', AlertType.error);
      },
    });
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.carregarMarcas();
  }

  public clickPesquisar(): void {
    this._paginaAtual = 1;
    this._dataSource.data = [];
    this.selection.clear();
    this.carregarMarcas(true);
  }

  public isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this._dataSource.data.length;
    return numSelected === numRows && numRows > 0;
  }

  public toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    this.selection.select(...this._dataSource.data);
  }

  public abrirModalExcluir(item?: MarcaStruct): void {
    const isBulk = !item;
    const itemsToDelete = isBulk ? this.selection.selected : [item!];

    if (itemsToDelete.length === 0) return;

    const hasLinkedProducts = itemsToDelete.some(m => m.possuiProdutos === true);

    const title = 'Confirmar exclusão';
    const description = hasLinkedProducts
      ? 'Existem produtos vinculados a algumas das marcas selecionadas. Confirmar a exclusão das marcas selecionadas?'
      : 'Confirma a exclusão das marcas selecionadas?';

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: title,
        description: description,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirm) {
        this._isLoading = true;

        if (isBulk) {
          const ids = itemsToDelete.map(i => i.idMarca);
          this.marcaService.deleteBulk(ids).subscribe({
            next: response => {
              this._isLoading = false;
              if (response.isError) {
                this.alertService.show(
                  'Atenção',
                  `Erro ao excluir marcas: ${response.errorDescription}`,
                  AlertType.warning
                );
              } else {
                this.alertService.show('Sucesso', 'Marcas excluídas com sucesso', AlertType.success);
                this.selection.clear();
                this.clickPesquisar();
              }
            },
            error: err => {
              this._isLoading = false;
              this.alertService.show(
                'Erro inesperado',
                err.message || 'Erro ao excluir marcas',
                AlertType.error
              );
            },
          });
        } else {
          this.marcaService.delete(item!.idMarca).subscribe({
            next: response => {
              this._isLoading = false;
              if (response.isError) {
                this.alertService.show(
                  'Erro',
                  response.errorDescription || 'Erro ao excluir marca',
                  AlertType.error
                );
              } else {
                this.alertService.show('Sucesso', 'Marca excluída com sucesso', AlertType.success);
                this.clickPesquisar();
              }
            },
            error: err => {
              this._isLoading = false;
              this.alertService.show(
                'Erro inesperado',
                err.message || 'Erro ao excluir marca',
                AlertType.error
              );
            },
          });
        }
      }
    });
  }
}
